/**
 * Admin Bridge Session schedule: list all bookings.
 * @feature BRIDGE_SESSION_BOOKING - Admin schedule view for Bridge Session bookings
 * Mark Complete: grants BRIDGE_SESSION entitlement (Control Bridge, Bridge Card, Exo Panel) per bridge_session.json
 */
import React, { useState, useEffect } from 'react';
import { Card, Table, Button, message, Spin, Empty, Alert } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { bridgeSessionBookingService, type BridgeSessionBooking } from '../services/bridgeSessionBookingService';
import { getApiErrorMessage } from '../utils/apiErrors';
import './BridgeSessionSchedulePage.css';

function httpStatusOf(err: unknown): number | undefined {
  if (!err || typeof err !== 'object') return undefined;
  const ax = err as { response?: { status?: number }; status?: number };
  return ax.response?.status ?? ax.status;
}

export const BridgeSessionSchedulePage: React.FC = () => {
  const [bookings, setBookings] = useState<BridgeSessionBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  const fetchBookings = async () => {
    setLoading(true);
    setAccessDenied(false);
    try {
      const list = await bridgeSessionBookingService.listAdminSchedule();
      setBookings(list);
    } catch (err: unknown) {
      const status = httpStatusOf(err);
      if (status === 403) {
        // Platform facilitator view — company users should use /upcoming-sessions instead.
        setAccessDenied(true);
        setBookings([]);
      } else {
        message.error(getApiErrorMessage(err, 'Failed to load schedule'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
  }, []);

  const handleMarkComplete = async (record: BridgeSessionBooking) => {
    try {
      setCompletingId(record.id);
      await bridgeSessionBookingService.complete(record.id);
      message.success(`Session marked complete. Company now has Control Bridge, Bridge Card, and Exo Panel access.`);
      await fetchBookings();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message: unknown }).message) : 'Failed to mark complete';
      message.error(msg);
    } finally {
      setCompletingId(null);
    }
  };

  const columns: ColumnsType<BridgeSessionBooking> = [
    {
      title: 'Date & Time',
      dataIndex: 'scheduled_at',
      key: 'scheduled_at',
      render: (val: string) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Person (User ID)',
      dataIndex: 'user_id',
      key: 'user_id',
      render: (val: string | undefined) => val || '—',
    },
    {
      title: 'Company',
      dataIndex: 'company_name',
      key: 'company_name',
      render: (val: string | undefined) => val || '—',
    },
    {
      title: 'Booking ID',
      dataIndex: 'id',
      key: 'id',
      ellipsis: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) =>
        (record.status || '').toLowerCase() === 'completed' ? (
          <span style={{ color: 'var(--color-success)' }}>Completed</span>
        ) : (
          <Button
            type="primary"
            size="small"
            loading={completingId === record.id}
            onClick={() => handleMarkComplete(record)}
            aria-label={`Mark session ${record.id} as complete`}
          >
            Mark Complete
          </Button>
        ),
    },
  ];

  return (
    <div className="bridge-session-schedule-page">
      <Card title="Bridge Session Schedule" className="bridge-session-schedule-card">
        {loading ? (
          <div className="bridge-session-schedule-loading">
            <Spin size="large" />
          </div>
        ) : accessDenied ? (
          <Empty
            description="This view lists all bookings across companies and is limited to facilitators."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16, textAlign: 'left' }}
              message="Looking for your own sessions?"
              description={
                <span>
                  Open <Link to="/upcoming-sessions">Session schedule</Link> under Company
                  administration to see bookings for your account.
                </span>
              }
            />
            <Link to="/upcoming-sessions">
              <Button type="primary">Go to my session schedule</Button>
            </Link>
          </Empty>
        ) : bookings.length === 0 ? (
          <Empty description="No bridge sessions scheduled" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={bookings}
            pagination={{ pageSize: 20, showSizeChanger: true }}
            size="middle"
          />
        )}
      </Card>
    </div>
  );
};

export default BridgeSessionSchedulePage;
